import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CheckCircle2, ExternalLink, FileSearch, FileText, Link as LinkIcon, RefreshCw, Search, Stethoscope, Unlink } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { api, getApiErrorMessage } from '../lib/api'
import { formatApiDateTimeDisplay } from '../lib/date'
import InlineError from '../ui/InlineError'

const INSURER_OPTIONS = [
  ['SAMSUNG_FIRE', '삼성화재'],
  ['LINA_LIFE', '라이나생명'],
  ['HYUNDAI_FIRE', '현대해상'],
  ['DB_FIRE', 'DB손해보험'],
  ['MERITZ_FIRE', '메리츠화재'],
  ['KB_FIRE', 'KB손해보험'],
  ['HANWHA_FIRE', '한화손해보험'],
  ['AXA_FIRE', 'AXA손해보험'],
  ['LOTTE_FIRE', '롯데손해보험'],
  ['HANA_FIRE', '하나손해보험'],
  ['HK_FIRE', '흥국화재'],
  ['AIG_FIRE', 'AIG손해보험'],
  ['SHINHAN_EZ_FIRE', '신한EZ손해보험'],
  ['CHUBB_FIRE', '처브손해보험'],
  ['MG_FIRE', 'MG손해보험'],
  ['CARROT_FIRE', '캐롯손해보험'],
]

const INSURER_LABELS = Object.fromEntries([
  ...INSURER_OPTIONS,
  ['DB', 'DB손해보험'],
  ['MERITZ', '메리츠화재'],
  ['KB_NONLIFE', 'KB손해보험'],
  ['HANWHA', '한화손해보험'],
  ['AXA', 'AXA손해보험'],
  ['AXA_NONLIFE', 'AXA손해보험'],
  ['LOTTE', '롯데손해보험'],
  ['LOTTE_NONLIFE', '롯데손해보험'],
  ['HANA', '하나생명'],
  ['HANA_NONLIFE', '하나손해보험'],
  ['HEUNGKUK_FIRE', '흥국화재'],
  ['AIG_KOREA', 'AIG손해보험'],
  ['SHINHAN_EZ', '신한EZ손해보험'],
  ['CHUBB_KOREA', '처브손해보험'],
  ['MG_NONLIFE', 'MG손해보험'],
  ['CARROT_NONLIFE', '캐롯손해보험'],
])

function unwrap(res) {
  const body = res?.data
  return body?.data !== undefined ? body.data : body
}

function toItems(value) {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.items)) return value.items
  if (Array.isArray(value?.candidates)) return value.candidates
  return []
}

function insurerLabel(code) {
  if (!code) return '-'
  return INSURER_LABELS[code] || '기타 보험사'
}

function formatDateTime(value) {
  return value ? formatApiDateTimeDisplay(undefined, value) : '-'
}

function formatNumber(value) {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : '-'
}

function chunkLabel(index) {
  const n = Number(index)
  return Number.isFinite(n) ? `본문 ${n + 1}` : '본문'
}

function matchReasonLabel(reason) {
  const value = String(reason || '').toLowerCase()
  if (value.includes('insurer')) return '보험사 일치'
  if (value.includes('product')) return '상품명 유사'
  if (value.includes('sale') || value.includes('date')) return '계약일 기준'
  if (value.includes('query') || value.includes('keyword')) return '검색어 일치'
  if (value.includes('status') || value.includes('parsed')) return '활용 가능'
  return '매칭 근거'
}

function scoreLabel(score) {
  const n = Number(score || 0)
  if (n >= 90) return '높음'
  if (n >= 60) return '보통'
  return '낮음'
}

function judgementLabel(status, fallback) {
  if (fallback) return fallback
  if (status === 'HIGH_POSSIBILITY') return '보장 가능성 높음'
  if (status === 'REVIEW_REQUIRED') return '검토 필요'
  if (status === 'TERMS_LINK_REQUIRED') return '약관 연결 필요'
  return '관련 낮음'
}

function judgementTone(status) {
  if (status === 'HIGH_POSSIBILITY') return 'green'
  if (status === 'REVIEW_REQUIRED') return 'blue'
  if (status === 'TERMS_LINK_REQUIRED') return 'amber'
  return 'slate'
}

function Badge({ children, tone = 'slate' }) {
  const toneClass = tone === 'green'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : tone === 'blue'
      ? 'border-sky-200 bg-sky-50 text-sky-700'
      : tone === 'amber'
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-slate-200 bg-slate-50 text-slate-700'
  return (
    <span className={`inline-flex min-h-[1.5rem] items-center rounded-full border px-2 text-xs font-semibold ${toneClass}`}>
      {children}
    </span>
  )
}

function EmptyState({ children }) {
  return (
    <div className="flex min-h-[9rem] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 text-center text-sm font-medium text-slate-500">
      {children}
    </div>
  )
}

function documentTitle(doc) {
  return doc?.productName || doc?.termsTitle || '약관 문서'
}

export default function PolicyLookupPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const customerRefId = searchParams.get('customerRefId') || ''
  const contractIdParam = searchParams.get('contractId') || ''

  const [ready, setReady] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [contracts, setContracts] = useState([])
  const [selectedContractId, setSelectedContractId] = useState(contractIdParam)
  const [contractMatch, setContractMatch] = useState(null)
  const [linkingDocumentId, setLinkingDocumentId] = useState(null)
  const [diseaseSearch, setDiseaseSearch] = useState({
    q: '',
    diseaseCode: '',
    scope: 'selected',
    limit: 5,
  })
  const [diseaseResult, setDiseaseResult] = useState(null)
  const [diseaseLoading, setDiseaseLoading] = useState(false)

  const [filters, setFilters] = useState({
    insurerCode: '',
    q: '',
    limit: 20,
  })
  const [documents, setDocuments] = useState([])
  const [documentMeta, setDocumentMeta] = useState(null)
  const [selectedDocumentId, setSelectedDocumentId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [chunks, setChunks] = useState([])
  const [chunkQuery, setChunkQuery] = useState('보험금')
  const [chunkResults, setChunkResults] = useState([])

  const selectedContract = useMemo(
    () => contracts.find((item) => String(item?.id) === String(selectedContractId)) || null,
    [contracts, selectedContractId],
  )

  const matchCandidates = useMemo(() => toItems(contractMatch?.candidates), [contractMatch])
  const diseaseContractResults = useMemo(() => toItems(diseaseResult?.contracts), [diseaseResult])
  const visibleDiseaseContractResults = useMemo(
    () => diseaseContractResults,
    [diseaseContractResults],
  )

  const displayedDocuments = useMemo(() => {
    if (matchCandidates.length > 0) {
      return matchCandidates.map((candidate) => ({ ...candidate.document, matchScore: candidate.score, matchReasons: candidate.reasons }))
    }
    return documents
  }, [documents, matchCandidates])

  const selectedDocument = useMemo(
    () => displayedDocuments.find((item) => String(item?.id) === String(selectedDocumentId)) || null,
    [displayedDocuments, selectedDocumentId],
  )
  const selectedContractTermsLink = selectedContract?.termsLink || contractMatch?.contract?.termsLink || null

  const loadReady = useCallback(async () => {
    try {
      const res = await api.get('/terms/integrated/ready', { __silent: true })
      setReady(unwrap(res) || null)
    } catch {
      setReady(null)
    }
  }, [])

  const loadContracts = useCallback(async () => {
    if (!customerRefId) {
      setContracts([])
      return
    }
    try {
      const res = await api.get(`/customer-management/customers/${customerRefId}/coverage-contracts`, { __silent: true })
      const data = unwrap(res) || {}
      const rows = Array.isArray(data.contracts) ? data.contracts : []
      setContracts(rows)
      setSelectedContractId((prev) => {
        if (prev && rows.some((row) => String(row.id) === String(prev))) return prev
        return rows[0]?.id ? String(rows[0].id) : ''
      })
    } catch (e) {
      setContracts([])
      setError(getApiErrorMessage(e, '계약 목록을 불러오지 못했습니다.'))
    }
  }, [customerRefId])

  const loadContractMatch = useCallback(async () => {
    if (!customerRefId || !selectedContractId) {
      setContractMatch(null)
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await api.get(
        `/customer-management/customers/${customerRefId}/coverage-contracts/${selectedContractId}/terms-matches`,
        { params: { limit: 5 }, __silent: true },
      )
      const data = unwrap(res) || null
      setContractMatch(data)
      const first = toItems(data?.candidates)[0]?.document
      setSelectedDocumentId(first?.id ?? null)
    } catch (e) {
      setContractMatch(null)
      setSelectedDocumentId(null)
      const status = e?.response?.status
      if (status === 404 || status === 500) {
        const message = '조회 가능한 약관이 없습니다.'
        setError(message)
        try { window?.toast?.show?.(message, 'info', 4000) } catch { }
        return
      }
      setError(getApiErrorMessage(e, '계약 기준 약관 연결에 실패했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [customerRefId, selectedContractId])

  const loadDocuments = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = { status: 'COMPLETED', limit: filters.limit }
      if (filters.insurerCode) params.insurerCode = filters.insurerCode
      if (filters.q.trim()) params.q = filters.q.trim()
      const res = await api.get('/terms/integrated/documents', { params })
      const payload = unwrap(res) || {}
      const rows = toItems(payload)
      setDocuments(rows)
      setDocumentMeta(payload)
      setContractMatch(null)
      setSelectedDocumentId(rows[0]?.id ?? null)
    } catch (e) {
      setDocuments([])
      setDocumentMeta(null)
      setError(getApiErrorMessage(e, '약관 검색에 실패했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [filters])

  const loadDocumentDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null)
      setChunks([])
      return
    }
    try {
      const [detailRes, chunksRes] = await Promise.all([
        api.get(`/terms/integrated/documents/${id}`),
        api.get(`/terms/integrated/documents/${id}/chunks`),
      ])
      setDetail(unwrap(detailRes) || null)
      setChunks(toItems(unwrap(chunksRes)))
    } catch (e) {
      setDetail(null)
      setChunks([])
      setError(getApiErrorMessage(e, '약관 상세 조회에 실패했습니다.'))
    }
  }, [])

  const searchChunks = async () => {
    const q = chunkQuery.trim()
    if (!q) return
    setLoading(true)
    setError('')
    try {
      const params = { q, limit: 20 }
      if (selectedDocumentId) params.documentId = selectedDocumentId
      else if (filters.insurerCode) params.insurerCode = filters.insurerCode
      const res = await api.get('/terms/integrated/chunks/search', { params })
      setChunkResults(toItems(unwrap(res)))
    } catch (e) {
      setChunkResults([])
      setError(getApiErrorMessage(e, '본문 검색에 실패했습니다.'))
    } finally {
      setLoading(false)
    }
  }

  const searchDiseaseTerms = async () => {
    if (!customerRefId) return
    const q = diseaseSearch.q.trim()
    const diseaseCode = diseaseSearch.diseaseCode.trim()
    if (!q && !diseaseCode) {
      setError('질병명 또는 질병코드를 입력해주세요.')
      return
    }
    setDiseaseLoading(true)
    setError('')
    try {
      const params = { limit: diseaseSearch.limit }
      if (q) params.q = q
      if (diseaseCode) params.diseaseCode = diseaseCode
      if (diseaseSearch.scope === 'selected' && selectedContractId) {
        params.contractId = selectedContractId
      }
      const res = await api.get(
        `/customer-management/customers/${customerRefId}/coverage-contracts/terms-disease-search`,
        { params },
      )
      const data = unwrap(res) || null
      setDiseaseResult(data)
      const firstDocument = toItems(data?.contracts)
        .flatMap((contract) => toItems(contract?.documents))
        .find((document) => document?.document?.id)
      if (firstDocument?.document?.id) {
        setSelectedDocumentId(firstDocument.document.id)
      }
    } catch (e) {
      setDiseaseResult(null)
      setError(getApiErrorMessage(e, '질병 기준 약관 검색에 실패했습니다.'))
    } finally {
      setDiseaseLoading(false)
    }
  }

  const linkSelectedContractTerms = async (doc) => {
    if (!customerRefId || !selectedContractId || !doc?.id) return
    setLinkingDocumentId(doc.id)
    setError('')
    try {
      const url = `/customer-management/customers/${customerRefId}/coverage-contracts/${selectedContractId}/terms-link`
      try {
        await api.put(url, { documentId: doc.id })
      } catch (e) {
        if (e?.response?.status !== 409) throw e
        const message = getApiErrorMessage(e, '선택한 약관의 계약 일치도를 확인해야 합니다.')
        const confirmed = window.confirm(`${message}\n그래도 이 약관으로 연결할까요?`)
        if (!confirmed) {
          setError(message)
          return
        }
        await api.put(url, { documentId: doc.id, force: true })
      }
      await loadContracts()
      await loadContractMatch()
      setSelectedDocumentId(doc.id)
    } catch (e) {
      setError(getApiErrorMessage(e, '확정 약관 연결에 실패했습니다.'))
    } finally {
      setLinkingDocumentId(null)
    }
  }

  const unlinkSelectedContractTerms = async () => {
    if (!customerRefId || !selectedContractId) return
    setLinkingDocumentId('unlink')
    setError('')
    try {
      await api.delete(`/customer-management/customers/${customerRefId}/coverage-contracts/${selectedContractId}/terms-link`)
      await loadContracts()
      await loadContractMatch()
    } catch (e) {
      setError(getApiErrorMessage(e, '확정 약관 연결 해제에 실패했습니다.'))
    } finally {
      setLinkingDocumentId(null)
    }
  }

  useEffect(() => {
    loadReady()
  }, [loadReady])

  useEffect(() => {
    setSelectedContractId(contractIdParam)
  }, [contractIdParam])

  useEffect(() => {
    loadContracts()
  }, [loadContracts])

  useEffect(() => {
    if (customerRefId && selectedContractId) {
      loadContractMatch()
    }
  }, [customerRefId, selectedContractId, loadContractMatch])

  useEffect(() => {
    if (!customerRefId) {
      loadDocuments()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadDocumentDetail(selectedDocumentId)
  }, [selectedDocumentId, loadDocumentDetail])

  const updateContractSelection = (value) => {
    setSelectedContractId(value)
    if (!customerRefId) return
    const next = new URLSearchParams(searchParams)
    next.set('customerRefId', customerRefId)
    if (value) next.set('contractId', value)
    else next.delete('contractId')
    setSearchParams(next, { replace: true })
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-slate-950">약관조회</h1>
            <Badge tone={ready?.status === 'READY' ? 'green' : 'amber'}>
              {ready?.status === 'READY' ? '통합약관 연결됨' : '연결 확인 필요'}
            </Badge>
          </div>
          <div className="mt-1 text-sm text-slate-500">
            {customerRefId ? '고객 계약 기준으로 공식 공시실 약관 후보를 확인합니다.' : '보험사와 상품명으로 공식 공시실 약관을 검색합니다.'}
          </div>
        </div>
        <button
          type="button"
          className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          onClick={() => {
            loadReady()
            if (customerRefId) loadContractMatch()
            else loadDocuments()
          }}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          새로고침
        </button>
      </div>

      {error ? <InlineError message={error} /> : null}

      {customerRefId ? (
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-base font-bold text-slate-950">
              <LinkIcon className="h-4 w-4 text-slate-500" />
              계약 기준 자동 연결
            </div>
            <Link className="text-sm font-semibold text-brand-navy hover:underline" to={`/support/customers/${customerRefId}`}>
              고객 상세
            </Link>
          </div>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
            <label>
              <span className="mb-1 block text-xs font-semibold text-slate-500">보유 계약</span>
              <select
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm"
                value={selectedContractId}
                onChange={(e) => updateContractSelection(e.target.value)}
              >
                {contracts.map((contract) => (
                  <option key={contract.id} value={contract.id}>
                    {[contract.insurerName, contract.productName, contract.contractDate].filter(Boolean).join(' · ')}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="inline-flex h-10 items-center justify-center gap-2 self-end rounded-lg bg-brand-navy px-4 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
              onClick={loadContractMatch}
              disabled={!selectedContractId || loading}
            >
              <Search className="h-4 w-4" />
              약관 연결
            </button>
          </div>
          {selectedContract ? (
            <div className="mt-3 space-y-2">
              <div className="grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm md:grid-cols-4">
                <div><span className="block text-xs font-semibold text-slate-500">보험사</span>{selectedContract.insurerName || '-'}</div>
                <div className="md:col-span-2"><span className="block text-xs font-semibold text-slate-500">상품명</span>{selectedContract.productName || '-'}</div>
                <div><span className="block text-xs font-semibold text-slate-500">계약일</span>{selectedContract.contractDate || '-'}</div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <Badge tone={selectedContractTermsLink?.linked ? 'green' : 'amber'}>
                    {selectedContractTermsLink?.linked ? '확정 약관 연결됨' : '확정 약관 미연결'}
                  </Badge>
                  {selectedContractTermsLink?.linked ? (
                    <span className="min-w-0 line-clamp-1 font-semibold text-slate-800">
                      {selectedContractTermsLink.productName || selectedContractTermsLink.termsTitle || '연결된 약관'}
                    </span>
                  ) : (
                    <span className="text-slate-500">후보 약관에서 실제 계약 약관을 확정 연결하세요.</span>
                  )}
                </div>
                {selectedContractTermsLink?.linked ? (
                  <button
                    type="button"
                    className="inline-flex h-8 items-center gap-1.5 rounded border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    onClick={unlinkSelectedContractTerms}
                    disabled={linkingDocumentId === 'unlink'}
                  >
                    <Unlink className="h-3.5 w-3.5" />
                    연결 해제
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </section>
      ) : (
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="grid gap-3 lg:grid-cols-[12rem_minmax(0,1fr)_8rem_auto]">
            <label>
              <span className="mb-1 block text-xs font-semibold text-slate-500">보험사</span>
              <select
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm"
                value={filters.insurerCode}
                onChange={(e) => setFilters((prev) => ({ ...prev, insurerCode: e.target.value }))}
              >
                <option value="">전체</option>
                {INSURER_OPTIONS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </label>
            <label>
              <span className="mb-1 block text-xs font-semibold text-slate-500">상품명 또는 약관명</span>
              <input
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
                value={filters.q}
                onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') loadDocuments()
                }}
                placeholder="예: 자동차보험, 실손"
              />
            </label>
            <label>
              <span className="mb-1 block text-xs font-semibold text-slate-500">건수</span>
              <select
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm"
                value={filters.limit}
                onChange={(e) => setFilters((prev) => ({ ...prev, limit: Number(e.target.value) }))}
              >
                {[20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button
              type="button"
              className="inline-flex h-10 items-center justify-center gap-2 self-end rounded-lg bg-brand-navy px-4 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
              onClick={loadDocuments}
              disabled={loading}
            >
              <Search className="h-4 w-4" />
              검색
            </button>
          </div>
        </section>
      )}

      {customerRefId ? (
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-base font-bold text-slate-950">
              <Stethoscope className="h-4 w-4 text-slate-500" />
              질병별 계약 약관 검색
            </div>
            {diseaseResult ? (
              <span className="text-xs font-semibold text-slate-500">
                {formatNumber(diseaseResult.matchedContractCount)} / {formatNumber(diseaseResult.contractCount)}건
              </span>
            ) : null}
          </div>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_11rem_9rem_7rem_auto]">
            <label>
              <span className="mb-1 block text-xs font-semibold text-slate-500">질병명</span>
              <input
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
                value={diseaseSearch.q}
                onChange={(e) => setDiseaseSearch((prev) => ({ ...prev, q: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') searchDiseaseTerms()
                }}
                placeholder="예: 암, 뇌졸중, 급성심근경색"
              />
            </label>
            <label>
              <span className="mb-1 block text-xs font-semibold text-slate-500">질병코드</span>
              <input
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
                value={diseaseSearch.diseaseCode}
                onChange={(e) => setDiseaseSearch((prev) => ({ ...prev, diseaseCode: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') searchDiseaseTerms()
                }}
                placeholder="예: C50.9"
              />
            </label>
            <label>
              <span className="mb-1 block text-xs font-semibold text-slate-500">범위</span>
              <select
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm"
                value={diseaseSearch.scope}
                onChange={(e) => setDiseaseSearch((prev) => ({ ...prev, scope: e.target.value }))}
              >
                <option value="selected">선택 계약</option>
                <option value="all">전체 계약</option>
              </select>
            </label>
            <label>
              <span className="mb-1 block text-xs font-semibold text-slate-500">본문</span>
              <select
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm"
                value={diseaseSearch.limit}
                onChange={(e) => setDiseaseSearch((prev) => ({ ...prev, limit: Number(e.target.value) }))}
              >
                {[3, 5, 10, 20].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button
              type="button"
              className="inline-flex h-10 items-center justify-center gap-2 self-end rounded-lg bg-brand-navy px-4 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
              onClick={searchDiseaseTerms}
              disabled={diseaseLoading || !customerRefId}
            >
              <Search className={`h-4 w-4 ${diseaseLoading ? 'animate-spin' : ''}`} />
              검색
            </button>
          </div>

          {visibleDiseaseContractResults.length ? (
            <div className="mt-4 space-y-3">
              {visibleDiseaseContractResults.map((contractResult) => {
                const contract = contractResult.contract || {}
                const documents = toItems(contractResult.documents)
                const matchedCoverages = toItems(contractResult.matchedCoverages)
                return (
                  <div key={contract.id || `${contract.insurerName}-${contract.productName}`} className="rounded-lg border border-slate-200 p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="line-clamp-1 text-sm font-bold text-slate-950">
                          {[contract.insurerName, contract.productName].filter(Boolean).join(' · ') || '보유 계약'}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">{contract.contractDate || '계약일 확인 필요'}</div>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <Badge tone={judgementTone(contractResult.judgementStatus)}>
                          {judgementLabel(contractResult.judgementStatus, contractResult.judgementLabel)}
                        </Badge>
                        <Badge tone={contractResult.termsLinked ? 'green' : 'amber'}>
                          {contractResult.termsLinked ? '약관 연결됨' : '약관 연결 필요'}
                        </Badge>
                      </div>
                    </div>
                    {matchedCoverages.length ? (
                      <div className="mt-3">
                        <div className="mb-1 text-xs font-bold text-slate-700">관련 담보</div>
                        <div className="grid gap-2 md:grid-cols-2">
                          {matchedCoverages.slice(0, 6).map((coverage) => (
                            <div key={coverage.id || `${coverage.coverageNameRaw}-${coverage.insuredAmount}`} className="rounded-lg border border-emerald-100 bg-emerald-50/70 px-3 py-2 text-xs">
                              <div className="flex items-start justify-between gap-2">
                                <span className="min-w-0 font-semibold text-emerald-950">{coverage.coverageNameRaw || coverage.coverageNameNormalized || '담보명 미입력'}</span>
                                <span className="shrink-0 font-bold tabular-nums text-emerald-800">{formatNumber(coverage.insuredAmount)}원</span>
                              </div>
                              {coverage.coverageGroup ? <div className="mt-0.5 text-emerald-700">{coverage.coverageGroup}</div> : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {documents.length ? (
                      <div className="mt-3 space-y-2">
                        {documents.map((documentResult) => {
                          const doc = documentResult.document || {}
                          return (
                            <div key={doc.id || doc.pdfUrl} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <button
                                  type="button"
                                  className="line-clamp-1 text-left text-sm font-bold text-slate-900 hover:text-brand-navy"
                                  onClick={() => setSelectedDocumentId(doc.id)}
                                >
                                  {documentTitle(doc)}
                                </button>
                                <div className="flex flex-wrap gap-1">
                                  <Badge tone={Number(documentResult.score) >= 90 ? 'blue' : 'amber'}>{scoreLabel(documentResult.score)}</Badge>
                                  {doc.pdfUrl ? (
                                    <a
                                      className="inline-flex min-h-[1.5rem] items-center gap-1 rounded-full border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                                      href={doc.pdfUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                      PDF
                                    </a>
                                  ) : null}
                                </div>
                              </div>
                              <div className="mt-2 grid gap-2 md:grid-cols-2">
                                {toItems(documentResult.matchedChunks).slice(0, diseaseSearch.limit).map((chunk) => (
                                  <button
                                    key={chunk.chunkId || `${chunk.documentId}-${chunk.chunkIndex}`}
                                    type="button"
                                    className="rounded-lg border border-slate-200 bg-white p-3 text-left hover:border-brand-sky/50 hover:bg-sky-50/40"
                                    onClick={() => setSelectedDocumentId(chunk.documentId)}
                                  >
                                    <div className="mb-1 text-xs font-semibold text-slate-500">{chunkLabel(chunk.chunkIndex)}</div>
                                    <div className="line-clamp-4 text-sm leading-6 text-slate-800">{chunk.text || '-'}</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <div className="mt-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-sm font-medium text-slate-500">
                        해당 질병으로 찾은 약관 본문이 없습니다.
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : null}
          {diseaseResult && !visibleDiseaseContractResults.length ? (
            <div className="mt-4">
              <EmptyState>관련 약관 본문을 찾지 못했습니다.</EmptyState>
            </div>
          ) : null}
        </section>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(28rem,1.05fr)]">
        <section className="rounded-lg border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div className="flex items-center gap-2 text-base font-bold text-slate-950">
              <FileSearch className="h-4 w-4 text-slate-500" />
              후보 약관
            </div>
            <span className="text-xs font-semibold text-slate-500">
              {matchCandidates.length > 0 ? `${matchCandidates.length}건` : `${formatNumber(documentMeta?.returnedCount ?? documents.length)}건`}
            </span>
          </div>
          <div className="max-h-[42rem] space-y-2 overflow-auto p-3">
            {displayedDocuments.map((doc) => {
              const active = String(doc?.id) === String(selectedDocumentId)
              const linkedToSelected = customerRefId && selectedContractTermsLink?.linked && String(selectedContractTermsLink.documentId) === String(doc?.id)
              return (
                <div
                  key={doc.id}
                  role="button"
                  tabIndex={0}
                  className={`block w-full rounded-lg border p-3 text-left transition ${active ? 'border-brand-sky bg-sky-50/50 ring-2 ring-sky-100' : 'border-slate-200 bg-white hover:border-sky-200 hover:bg-sky-50/30'}`}
                  onClick={() => setSelectedDocumentId(doc.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setSelectedDocumentId(doc.id)
                    }
                  }}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge>{insurerLabel(doc.insurerCode)}</Badge>
                      <Badge tone="green">파싱 완료</Badge>
                      {doc.matchScore != null ? <Badge tone={Number(doc.matchScore) >= 90 ? 'blue' : 'amber'}>{scoreLabel(doc.matchScore)}</Badge> : null}
                      {linkedToSelected ? <Badge tone="green">확정 연결됨</Badge> : null}
                    </div>
                    {customerRefId && selectedContractId ? (
                      <button
                        type="button"
                        className={`inline-flex h-8 items-center gap-1.5 rounded border px-2 text-xs font-semibold disabled:opacity-50 ${linkedToSelected ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-sky-200 bg-white text-brand-navy hover:bg-sky-50'}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (!linkedToSelected) linkSelectedContractTerms(doc)
                        }}
                        disabled={linkedToSelected || linkingDocumentId === doc.id}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {linkedToSelected ? '연결됨' : '확정 연결'}
                      </button>
                    ) : null}
                  </div>
                  <div className="mt-2 line-clamp-2 text-sm font-bold text-slate-950">{documentTitle(doc)}</div>
                  <div className="mt-1 line-clamp-2 text-xs text-slate-500">{doc.termsTitle || '-'}</div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                    <span>{doc.salePeriodText || '판매기간 확인 필요'}</span>
                    <span>페이지 {formatNumber(doc.pageCount)}</span>
                    <span>본문 {formatNumber(doc.chunkCount)}</span>
                  </div>
                  {Array.isArray(doc.matchReasons) && doc.matchReasons.length ? (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {doc.matchReasons.slice(0, 4).map((reason) => <Badge key={reason}>{matchReasonLabel(reason)}</Badge>)}
                    </div>
                  ) : null}
                </div>
              )
            })}
            {!displayedDocuments.length ? <EmptyState>조회된 약관이 없습니다.</EmptyState> : null}
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4">
          {!selectedDocumentId ? (
            <EmptyState>약관을 선택하세요.</EmptyState>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="line-clamp-2 text-lg font-bold text-slate-950">{detail?.productName || selectedDocument?.productName || '-'}</h2>
                  <p className="mt-1 line-clamp-2 text-sm text-slate-500">{detail?.termsTitle || selectedDocument?.termsTitle || '-'}</p>
                </div>
                {(detail?.pdfUrl || selectedDocument?.pdfUrl) ? (
                  <a
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    href={detail?.pdfUrl || selectedDocument?.pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLink className="h-4 w-4" />
                    PDF
                  </a>
                ) : null}
              </div>

              <div className="grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm md:grid-cols-2">
                <div><span className="block text-xs font-semibold text-slate-500">보험사</span>{insurerLabel(detail?.insurerCode || selectedDocument?.insurerCode)}</div>
                <div><span className="block text-xs font-semibold text-slate-500">판매기간</span>{detail?.salePeriodText || selectedDocument?.salePeriodText || '-'}</div>
                <div><span className="block text-xs font-semibold text-slate-500">수집일시</span>{formatDateTime(detail?.collectedAt || selectedDocument?.collectedAt)}</div>
                <div><span className="block text-xs font-semibold text-slate-500">본문</span>{formatNumber(detail?.chunkCount || selectedDocument?.chunkCount)}개</div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
                    <FileText className="h-4 w-4 text-slate-500" />
                    약관 본문
                  </div>
                  <span className="text-xs text-slate-500">{formatNumber(chunks.length)}건</span>
                </div>
                <div className="max-h-[24rem] space-y-2 overflow-auto pr-1">
                  {chunks.slice(0, 30).map((chunk) => (
                    <div key={chunk.chunkId || `${chunk.documentId}-${chunk.chunkIndex}`} className="rounded-lg border border-slate-200 p-3">
                      <div className="mb-1 text-xs font-semibold text-slate-500">{chunkLabel(chunk.chunkIndex)}</div>
                      <div className="line-clamp-5 text-sm leading-6 text-slate-800">{chunk.text || '-'}</div>
                    </div>
                  ))}
                  {!chunks.length ? <EmptyState>파싱된 본문이 없습니다.</EmptyState> : null}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-base font-bold text-slate-950">
            <Search className="h-4 w-4 text-slate-500" />
            본문 검색
          </div>
          <button
            type="button"
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand-navy px-3 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
            onClick={searchChunks}
            disabled={loading}
          >
            <Search className="h-4 w-4" />
            검색
          </button>
        </div>
        <input
          className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
          value={chunkQuery}
          onChange={(e) => setChunkQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') searchChunks()
          }}
          placeholder="보험금, 면책, 감액 등"
        />
        <div className="mt-3 grid gap-2 md:grid-cols-2">
          {chunkResults.slice(0, 8).map((chunk) => (
            <button
              key={chunk.chunkId || `${chunk.documentId}-${chunk.chunkIndex}`}
              type="button"
              className="rounded-lg border border-slate-200 p-3 text-left hover:border-brand-sky/50 hover:bg-sky-50/40"
              onClick={() => setSelectedDocumentId(chunk.documentId)}
            >
              <div className="mb-1 text-xs font-semibold text-slate-500">{chunkLabel(chunk.chunkIndex)}</div>
              <div className="line-clamp-4 text-sm leading-6 text-slate-800">{chunk.text || '-'}</div>
            </button>
          ))}
        </div>
      </section>
    </div>
  )
}
